import { z } from 'zod';

// CSV → deals mapping (Spec V1.1 §M10 L1515-1527 + Modele V1.2 §4.2).
// The downloadable template uses the friendly column names below; this map
// translates them to the `deals.*` column names expected by the RPC payload.
//
// Friendly columns kept on purpose so non-technical admins can edit the CSV
// without memorising the DB schema. Anything not in the map is ignored.
export const CSV_HEADER_MAP: Record<string, string> = {
  type: 'type',
  zone: 'zone',
  community: 'community',
  building: 'building',
  // ADR-027 — property_type. DLD CSV exports use "Property Sub Type"; we
  // accept that variant alongside the canonical key, since canonicalHeader()
  // already lowercases + trims so the dictionary check is exact.
  property_type: 'property_type',
  'property sub type': 'property_type',
  bedrooms: 'bedrooms',
  area: 'area_sqft', // 🔹 Déduit (ADR-021): Spec says 'area' optional, Modele forces NOT NULL.
  bathrooms: 'bathrooms',
  baths: 'bathrooms',
  floor: 'floor',
  handover: 'handover',
  title: 'title',
  description: 'description',
  price_aed: 'asking_price',
  asking_price: 'asking_price',
  original_price: 'original_price',
  amount_paid: 'amount_paid',
};

export const CSV_TEMPLATE_HEADERS = [
  'type',
  'zone',
  'community',
  'building',
  'property_type',
  'bedrooms',
  'area',
  'bathrooms',
  'floor',
  'handover',
  'title',
  'description',
  'price_aed',
  'original_price',
  'amount_paid',
];

// Subset of headers without which `csvDealRowSchema` cannot validate a row.
// Catches the "admin removed a column from the template" mistake before the
// preview is even rendered (ADR-022 §D3).
export const REQUIRED_CSV_HEADERS = [
  'type',
  'zone',
  'community',
  'bedrooms',
  'area',
  'title',
  'description',
  'price_aed',
] as const;

// Header tolerance: lowercases + trims a raw header for matching against the
// canonical CSV_HEADER_MAP keys. Excel exports often capitalise the first row
// (`Type`, `Zone`...) or leave a trailing space — without normalisation those
// columns would silently disappear in `mapCsvRow`.
function canonicalHeader(raw: string): string {
  return raw.trim().toLowerCase();
}

export type HeaderValidationResult = {
  missing: string[]; // canonical names from REQUIRED_CSV_HEADERS that don't appear
  extra: string[]; // raw header tokens that don't match any CSV_HEADER_MAP key
  /** Map from a raw header string in the file → its canonical key (after lowercase/trim). */
  normalizedMap: Record<string, string>;
};

/**
 * Inspect the CSV header line and report missing required columns + extra
 * columns the importer will ignore. Tolerant to casing and whitespace.
 *
 * The function does NOT throw — it returns a structured result the UI uses
 * to decide whether to render the preview or surface a header banner first.
 */
export function validateHeaders(headers: string[]): HeaderValidationResult {
  const normalizedMap: Record<string, string> = {};
  const present = new Set<string>();
  const extra: string[] = [];

  for (const raw of headers) {
    const canonical = canonicalHeader(raw);
    if (canonical in CSV_HEADER_MAP) {
      normalizedMap[raw] = canonical;
      present.add(canonical);
    } else {
      extra.push(raw);
    }
  }

  const missing = REQUIRED_CSV_HEADERS.filter((h) => !present.has(h));
  return { missing, extra, normalizedMap };
}

// Normalize raw CSV cell → JS value before Zod sees it. Empty strings become
// undefined so optional schemas accept them; everything else is left intact
// for Zod's `coerce` to handle (numbers parse via `Number(value)`).
function trimToUndef(v: string | undefined): string | undefined {
  if (v === undefined) return undefined;
  const trimmed = v.trim();
  return trimmed === '' ? undefined : trimmed;
}

export type RawCsvRow = Record<string, string>;
export type MappedCsvRow = Record<string, string | undefined>;

/**
 * Take a row keyed by CSV column names and produce a row keyed by DB column
 * names, trimming each cell and dropping unknown columns. Header lookup is
 * case-insensitive and whitespace-tolerant: `Type`, ` type `, `TYPE` all map.
 */
export function mapCsvRow(row: RawCsvRow): MappedCsvRow {
  const out: MappedCsvRow = {};
  const lookup: Record<string, string> = {};
  for (const k of Object.keys(row)) {
    lookup[canonicalHeader(k)] = row[k];
  }
  for (const [csvKey, dbKey] of Object.entries(CSV_HEADER_MAP)) {
    if (csvKey in lookup) {
      out[dbKey] = trimToUndef(lookup[csvKey]);
    }
  }
  return out;
}

const optionalString = z
  .string()
  .max(120)
  .optional()
  .transform((v) => (v === undefined ? null : v));

const optionalInt = z
  .union([z.string(), z.number()])
  .optional()
  .transform((v) => {
    if (v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  });

const optionalPrice = z
  .union([z.string(), z.number()])
  .optional()
  .transform((v) => {
    if (v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });

export const csvDealRowSchema = z
  .object({
    type: z.enum(['ready', 'off_plan']),
    zone: z.string().min(1).max(80),
    community: z.string().min(1).max(100),
    building: optionalString,
    // ADR-027 — optional in the CSV (DEFAULT 'apartment' applies server-side
    // when missing). Strict lowercase enum: admin must downcase Excel-style
    // 'Apartment'/'Villa' before import. Documented in the template.
    property_type: z
      .enum(['apartment', 'villa', 'townhouse', 'penthouse', 'plot', 'commercial'])
      .optional(),
    bedrooms: z.coerce.number().int().min(0).max(10),
    // 🔹 Déduit (ADR-021): area_sqft required because Modele §4.2 has it
    // NOT NULL even though Spec §M10 lists `area` as optional.
    area_sqft: z.coerce.number().int().min(100).max(50000),
    bathrooms: optionalInt.refine((v) => v === null || (v >= 1 && v <= 15), {
      message: 'bathrooms_range::must be between 1 and 15',
    }),
    floor: optionalInt,
    handover: z
      .string()
      .max(20)
      .optional()
      .transform((v) => (v === undefined ? null : v)),
    title: z.string().min(10).max(120),
    description: z.string().min(50),
    asking_price: z.coerce.number().gt(0),
    original_price: optionalPrice,
    amount_paid: optionalPrice,
  })
  .superRefine((row, ctx) => {
    if (row.type === 'off_plan') {
      if (row.original_price === null) {
        ctx.addIssue({
          path: ['original_price'],
          code: 'custom',
          message: 'required_for_off_plan::required for off_plan',
        });
      }
      if (row.amount_paid === null) {
        ctx.addIssue({
          path: ['amount_paid'],
          code: 'custom',
          message: 'required_for_off_plan::required for off_plan',
        });
      }
    }
    // Mirrors DB CHECK deals_amount_paid_check (003_deals.sql §142): you
    // cannot have paid more than the original sale price.
    if (
      row.original_price !== null &&
      row.amount_paid !== null &&
      row.amount_paid > row.original_price
    ) {
      ctx.addIssue({
        path: ['amount_paid'],
        code: 'custom',
        message: 'amount_paid_exceeds_original::must be ≤ original_price',
      });
    }
    // Anti-pattern distress (ADR-022 §D2): a distress deal is by definition
    // priced below the original sale value. asking_price > original_price
    // doesn't violate any DB CHECK but it does invert the value proposition.
    // Hard-reject here so the admin spots the typo (e.g. swapped columns)
    // before the row reaches production.
    if (row.original_price !== null && row.asking_price > row.original_price) {
      ctx.addIssue({
        path: ['asking_price'],
        code: 'custom',
        message: 'asking_exceeds_original::must be ≤ original_price (distress deal)',
      });
    }
  });

export type CsvDealRow = z.infer<typeof csvDealRowSchema>;

// Intra-file dedup (ADR-021): two CSV rows are considered duplicates when the
// tuple below matches. We deliberately do NOT check against existing deals in
// the database — that's an editorial decision for the admin, not a hard
// validation rule.
export function dedupKey(row: CsvDealRow): string {
  return [
    row.type,
    row.zone.toLowerCase(),
    row.community.toLowerCase(),
    (row.building ?? '').toLowerCase(),
    row.bedrooms,
    row.asking_price,
  ].join('|');
}

// Structured per-row error. The `code` is the stable token i18n maps to a
// human message; `message` is the fallback Zod produced (kept for tests and
// for the rare case where the UI has no key for the code).
export type CsvRowError = {
  path: string;
  code: string;
  message: string;
};

// Outcome of parsing a single CSV row before the RPC call. Both variants carry
// the raw row so the preview UI can render the admin's original values next
// to the error message even when the row fails validation.
export type ParsedCsvRow =
  | {
      index: number;
      ok: true;
      data: CsvDealRow;
      dedupKey: string;
      rawRow: RawCsvRow;
    }
  | {
      index: number;
      ok: false;
      errors: CsvRowError[];
      rawRow: RawCsvRow;
    };

type ZodIssueLike = {
  code: string;
  path: PropertyKey[];
  message: string;
};

// Convention to embed a stable token in custom Zod issue messages, since
// Zod v4 no longer carries a `params` field: format the message as
// `"<csvCode>::<human message>"`. Built-in codes (too_small, too_big,
// invalid_type…) come straight from Zod and don't use the sentinel.
const CSV_CODE_SENTINEL = '::';

function zodIssueToCsvError(iss: ZodIssueLike): CsvRowError {
  const path =
    iss.path.length === 0
      ? 'row'
      : iss.path.map((p) => (typeof p === 'symbol' ? p.toString() : String(p))).join('.');
  let code: string = iss.code;
  let message = iss.message;
  if (iss.code === 'custom' && iss.message.includes(CSV_CODE_SENTINEL)) {
    const sepIndex = iss.message.indexOf(CSV_CODE_SENTINEL);
    code = iss.message.slice(0, sepIndex);
    message = iss.message.slice(sepIndex + CSV_CODE_SENTINEL.length);
  } else if (iss.code === 'invalid_type' && iss.message.toLowerCase().includes('required')) {
    code = 'required';
  } else if (iss.code === 'too_small') {
    code = 'too_small';
  } else if (iss.code === 'too_big') {
    code = 'too_big';
  }
  return { path, code, message };
}

/**
 * Validate every (raw, mapped) pair in turn. Rows that fail Zod produce an
 * `ok=false` entry with structured `CsvRowError[]`. Rows that pass but collide
 * with an earlier valid row's dedup key are turned into `ok=false` with a
 * single `duplicate_in_file` error. `rawRow` is always preserved so the
 * preview can echo the admin's original values back, even on failure.
 */
export function validateRows(pairs: { raw: RawCsvRow; mapped: MappedCsvRow }[]): ParsedCsvRow[] {
  const seen = new Set<string>();
  const out: ParsedCsvRow[] = [];

  pairs.forEach(({ raw, mapped }, idx) => {
    const parsed = csvDealRowSchema.safeParse(mapped);
    if (!parsed.success) {
      const errors = parsed.error.issues.map((iss) => zodIssueToCsvError(iss));
      out.push({ index: idx, ok: false, errors, rawRow: raw });
      return;
    }
    const key = dedupKey(parsed.data);
    if (seen.has(key)) {
      out.push({
        index: idx,
        ok: false,
        errors: [
          {
            path: 'row',
            code: 'duplicate_in_file',
            message: 'duplicate_in_file',
          },
        ],
        rawRow: raw,
      });
      return;
    }
    seen.add(key);
    out.push({ index: idx, ok: true, data: parsed.data, dedupKey: key, rawRow: raw });
  });

  return out;
}
